import { Component, computed, inject, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { updateVisitHistoryData } from '../../../../../store/global/global.actions';
import { SubscriptionComponent } from '../../../../shared/helpers/subscription.component';
import { ActivatedRoute, Router } from '@angular/router';
import { VoucherGridItemObject } from '@it2go/types';
import { VoucherDetailPageStore } from './voucher-detail-page.store';
import { FilterBuilder } from '../../../helper/filter.builder';
import { VoucherTypeEnum } from '../../../../../../../../types/src/common/enum/voucher-type.enum';
import { selectFolder } from '../../../store/person/person.selectors';
import { filter, Observable, switchMap } from 'rxjs';
import { VoucherDetailService } from './voucher-detail-page.service';
import { VoucherTab, VoucherTabs } from './voucher-detail-page.types';
import { VoucherDetailPageStore2 } from './voucher-detail-page.store2';
import { PersonContactTypeEnum } from '../../../../../../../../types/src/services/enum/person-contact-type.enum';
import {
    VoucherDetailSituationDpComponent,
} from '../../../components/person/voucher-detail-situation/voucher-detail-situation-dp/voucher-detail-situation-dp.component';
import {
    VoucherDetailSituationOrpComponent,
} from '../../../components/person/voucher-detail-situation/voucher-detail-situation-orp/voucher-detail-situation-orp.component';
import {
    VoucherDetailSituationFtComponent,
} from '../../../components/person/voucher-detail-situation/voucher-detail-situation-ft/voucher-detail-situation-ft.component';
import { AsyncPipe } from '@angular/common';
import { LoadingComponent } from '../../../../../components/kit/loading/loading.component';
import { Card } from '@openng/optimus-ui/card';
import { Tab, TabList, TabPanel, TabPanels, Tabs } from '@openng/optimus-ui/tabs';
import {
    VoucherDetailGeneralComponent,
} from '../../../components/person/voucher-detail-general/voucher-detail-general.component';
import {
    VoucherDetailPrescriptionsComponent,
} from '../../../components/person/voucher-detail-prescriptions/voucher-detail-prescriptions.component';
import { ActionButtonsComponent } from '../../../../../components/kit/action-buttons/action-buttons.component';
import { FolderGlobalStore } from '../../../../../store/medicalService/folder.global-store';

@Component({
    selector: 'app-voucher-detail-page',
    templateUrl: 'voucher-detail-page.component.html',
    styleUrls: ['voucher-detail-page.component.scss'],
    providers: [VoucherDetailPageStore, VoucherDetailService, VoucherDetailPageStore2],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        AsyncPipe,
        LoadingComponent,
        Card,
        Tabs,
        TabList,
        Tab,
        TabPanels,
        TabPanel,
        VoucherDetailGeneralComponent,
        VoucherDetailSituationDpComponent,
        VoucherDetailSituationOrpComponent,
        VoucherDetailSituationFtComponent,
        VoucherDetailPrescriptionsComponent,
        ActionButtonsComponent,
    ],
})
export class VoucherDetailPageComponent extends SubscriptionComponent {

    public readonly store2 = inject(VoucherDetailPageStore2);
    protected readonly folderStore = inject(FolderGlobalStore);
    protected readonly store = inject(VoucherDetailPageStore);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    public readonly detailService = inject(VoucherDetailService);


    public readonly situationDP = viewChild<VoucherDetailSituationDpComponent>(VoucherDetailSituationDpComponent);
    public readonly situationORP = viewChild<VoucherDetailSituationOrpComponent>(VoucherDetailSituationOrpComponent);
    public readonly situationFT = viewChild<VoucherDetailSituationFtComponent>(VoucherDetailSituationFtComponent);

    private wysiwygs = computed(() => {
        return [
            ...this.situationDP() ? this.situationDP()?.wysiwygs() ?? [] : [],
            ...this.situationORP() ? this.situationORP()?.wysiwygs() ?? [] : [],
            ...this.situationFT() ? this.situationFT()?.wysiwygs() ?? [] : [],
        ];
    });

    voucher: VoucherGridItemObject | null = null;

    tabs!: VoucherTabs[];

    formInvalid$!: Observable<boolean>;

    public readonly voucher$ = this.detailService.voucher();

    constructor() {
        super();

        this.subs.push(
            this.store.globalStore!.select(selectFolder).subscribe((folder) => {
                if (folder) {
                    this.store.api.getPersonContacts(
                        FilterBuilder.singlePage({
                            personId: folder.person.id,
                            type: PersonContactTypeEnum.CONTACT,
                        }),
                    );
                }
            }),
        );

        this.formInvalid$ = this.detailService.getFormValidation();

        this.subs.push(
            this.store.routeParam.voucherId.pipe(
                filter((voucherId) => !!voucherId),
                switchMap((id: string) => {
                    if (id?.startsWith('create-')) {
                        const voucherType: VoucherTypeEnum = id
                            .substring('create-'.length)
                            .toUpperCase() as VoucherTypeEnum;
                        return this.detailService.newVoucher(voucherType);
                    } else {
                        this.store.globalStore?.dispatch(updateVisitHistoryData());
                        return this.detailService.loadVoucher(Number(id));
                    }
                }),
            ).subscribe(() => {
                this.tabs = this.detailService.invalidationInTabs();
                this.store.globalStore?.dispatch(updateVisitHistoryData());
            }),
        );
    }

    cancel() {
        return this.router.navigate(['../'], { relativeTo: this.route });
    }

    async submit() {
        this.wysiwygs()?.forEach((it) => it.resetBackupValues());
        this.detailService.saveVoucher();
    }

    protected readonly VoucherTab = VoucherTab;
}
